/**
 * Iter 89.4 / Sprint 2A.3 — HistoricalSparkline
 * ─────────────────────────────────────────────────────────────────
 * Compact 7-bar sparkline used as a column on the
 * /workforce/desktop-activity dashboard. One bar per day; height
 * proportional to that day's `active_seconds`. Idle and locked are
 * NOT visualised — the operator brief wants a single "how many
 * hours were they active this past week?" glance, with idle/locked
 * remaining accessible via the drill-down modal.
 *
 * Pure presentational. Stateless. Takes the
 * /desktop-activity/dashboard/historical → registrations[i].daily_series
 * array verbatim.
 */

const MAX_BAR_HEIGHT_PX = 28;

function fmtSecsCompact(s) {
    const total = Number(s) || 0;
    if (total === 0) return "0h";
    const h = total / 3600;
    if (h >= 1) return `${h.toFixed(1)}h`;
    return `${Math.round(total / 60)}m`;
}

export function HistoricalSparkline({ series = [], testId }) {
    if (!series.length) {
        return (
            <span
                className="inline-block text-[10px] text-slate-300 font-mono"
                data-testid={testId}
            >
                no data
            </span>
        );
    }
    // Scale to the MAX active_seconds in this row's series.
    const maxActive = series.reduce(
        (m, d) => Math.max(m, Number(d.active_seconds) || 0),
        0,
    );
    const safeMax = maxActive > 0 ? maxActive : 1;
    return (
        <div
            className="inline-flex items-end gap-[2px] h-7"
            data-testid={testId}
            aria-label="7-day active-hours sparkline"
        >
            {series.map((d) => {
                const active = Number(d.active_seconds) || 0;
                const ratio = active / safeMax;
                const h = Math.max(2, Math.round(ratio * MAX_BAR_HEIGHT_PX));
                const isZero = active === 0;
                return (
                    <span
                        key={d.date}
                        className={`block w-1.5 rounded-sm ${
                            isZero ? "bg-slate-200" : "bg-emerald-500"
                        }`}
                        style={{ height: `${h}px` }}
                        title={`${d.date} · active ${fmtSecsCompact(d.active_seconds)} · idle ${fmtSecsCompact(d.idle_seconds)} · locked ${fmtSecsCompact(d.locked_seconds)}`}
                        data-date={d.date}
                        data-active-seconds={active}
                    />
                );
            })}
        </div>
    );
}

export default HistoricalSparkline;
